import type { OutcomeTier } from "./card";

// ---------------------------------------------------------------------------
// Resolution types (used by the odds engine)
// ---------------------------------------------------------------------------

export interface PackOdds {
  common: number;
  uncommon: number;
  rare: number;
  epic: number;
  legendary: number;
  mythic: number;
  grail: number;
  /** The God Pack: one roll, five cards (rare, epic, legendary, mythic, grail). */
  god: number;
}

/** Keys of the resolution odds table (all eight outcome tiers). */
export type PackOddsTier = keyof PackOdds;

export interface PackOpen {
  id: string;
  packId: string;
  userId: string;
  streamId?: string;
  cards: string[];
  /** Links to the full audit event when available */
  eventId?: string;
  openedAt: Date;
}

// ---------------------------------------------------------------------------
// Audit types — every pack opening produces a full event for traceability
// ---------------------------------------------------------------------------

/**
 * The complete audit record of a single pack opening.
 * Every RNG decision is captured so that the opening can be independently
 * replayed and verified.
 */
export interface PackOpenEvent {
  id: string;
  packId: string;
  userId: string;
  streamId?: string;
  results: PackOpenCardResult[];
  /** Optional provable-fairness seed (for future commit-reveal scheme) */
  seed?: string;
  openedAt: Date;
}

/**
 * Per-slot audit record within a pack opening.
 */
export interface PackOpenCardResult {
  slotIndex: number;
  /** Raw RNG value [0,1) used for the categorical outcome-tier roll. */
  rarityRoll: number;
  /** Same as `rarityRoll` for tier-first audits (stable alias). */
  tierRoll?: number;
  /** Outcome tier resolved for this slot (V/P bands + tier roll). */
  resolvedRarity: OutcomeTier;
  cardId: string;
  /** How many cards were eligible in the pool for this slot. */
  cardPoolSize: number;
  /** Raw RNG value [0,1) used to select the SKU within the rolled tier. */
  cardSelectionRoll: number;
}

// ---------------------------------------------------------------------------
// Drop table — defines odds + card pool filtering for a pack
// ---------------------------------------------------------------------------

export interface DropTable {
  packId: string;
  sport: string;
  setName: string;
  odds: PackOdds;
  cardPoolFilter?: {
    sport?: string;
    setName?: string;
    year?: number;
  };
}

// ---------------------------------------------------------------------------
// Catalog / display types (pack catalog system)
// ---------------------------------------------------------------------------

/** All valid pack lifecycle statuses (single source of truth). */
export const PACK_STATUSES = ["draft", "scheduled", "active", "sold_out", "retired"] as const;

/** Lifecycle status of a pack in the catalog. */
export type PackStatus = (typeof PACK_STATUSES)[number];

/** All valid pack categories (single source of truth). */
export const PACK_CATEGORIES = ["standard", "premium", "deluxe", "promo", "limited"] as const;

/** Pricing / positioning category. */
export type PackCategory = (typeof PACK_CATEGORIES)[number];

/** Template / pool-filter slug: all sports cards in the pool, excluding Pokémon. */
export const MULTISPORT_POOL_SPORT = "multisport" as const;

/** Catalog type slug for Pokémon / TCG (excluded from multisport pools). */
export const POKEMON_POOL_SPORT = "pokemon" as const;

/** Slug meaning no sport restriction (legacy / catch-all). */
export const MIXED_POOL_SPORT = "mixed" as const;

export function isMultisportPoolSport(sport: string | null | undefined): boolean {
  return sport?.trim().toLowerCase() === MULTISPORT_POOL_SPORT;
}

/** True when pool eligibility should not filter to a single `cards.sport`. */
export function isUnrestrictedPoolSport(sport: string | null | undefined): boolean {
  const t = sport?.trim().toLowerCase();
  if (!t) return true;
  return t === MIXED_POOL_SPORT;
}

/** @deprecated Use {@link isUnrestrictedPoolSport} or {@link isMultisportPoolSport}. */
export const CROSS_SPORT_POOL_SPORTS = [MIXED_POOL_SPORT, MULTISPORT_POOL_SPORT] as const;

/** @deprecated Use {@link isUnrestrictedPoolSport} or {@link isMultisportPoolSport}. */
export function isCrossSportPoolSport(sport: string | null | undefined): boolean {
  return isUnrestrictedPoolSport(sport) || isMultisportPoolSport(sport);
}

/** JSON filter for which inventory SKUs back a template (builder). */
export type PoolFilterJson = {
  sport?: string;
  setName?: string;
  yearMin?: number;
  yearMax?: number;
  gradingAuthority?: string;
  /** Numeric PSA grade bounds, inclusive. Grades are stored as text ("PSA 10"). */
  gradeMin?: number;
  gradeMax?: number;
  /** Substring match on the catalog player name (the character name for Pokemon). */
  playerName?: string;
  /** Substring match on the catalog variety (parallel / treatment), e.g. "ART RARE". */
  variety?: string;
  cardIds?: string[];
  excludeCardIds?: string[];
};

/** Overrides global 7-tier Curve L probabilities + V/P bands (stored on pack_templates). */
export type DistributionOverrideJson = {
  probabilities?: Partial<Record<OutcomeTier, number>>;
  bands?: Partial<Record<OutcomeTier, readonly [number, number]>>;
  chaseCeilingMultiple?: number;
};

export type SafetyCheckStatus = "green" | "yellow" | "red" | "unknown";

/** Optional storefront SEO overrides stored on `pack_templates.seo_meta`. */
export interface PackSeoMeta {
  title?: string;
  description?: string;
  og_image_url?: string;
  no_index?: boolean;
}

/** Image set attached to a pack for display purposes. */
export interface PackImages {
  /** Primary pack art (front of pack). */
  front: string;
  /** Rear pack art (optional). */
  back?: string;
  /** Small image used in list / grid views. */
  thumbnail: string;
  /** Wide promotional banner (optional). */
  banner?: string;
}

/** A guaranteed pull slot — e.g. "1 guaranteed rare or better". */
export interface GuaranteedSlot {
  minRarity: OutcomeTier;
  count: number;
}

/**
 * Controls when and how many of a pack can be purchased.
 *
 * Supply semantics:
 * - When `maxSupply` is defined, `currentSupply` tracks remaining units and
 *   the availability engine checks it before allowing purchases.
 * - When `maxSupply` is undefined (unlimited), `currentSupply` is irrelevant
 *   and defaults to 0. The engine skips supply checks in this case.
 */
export interface PackAvailability {
  /** When the pack goes on sale (undefined = immediately). */
  releaseDate?: Date;
  /** When the pack is removed from sale (undefined = never). */
  endDate?: Date;
  /** Maximum units that will ever exist (undefined = unlimited). */
  maxSupply?: number;
  /**
   * Units still available for purchase.
   * Only meaningful when `maxSupply` is set; defaults to 0 for unlimited packs.
   */
  currentSupply: number;
}

/**
 * User-facing odds display line.
 * Separated from resolution odds so the presentation layer can phrase odds
 * however it likes without coupling to the engine.
 */
export interface PackOddsDisplay {
  /** Human-readable label, e.g. "1 in 20 packs". */
  label: string;
  /** Which odds-table tier this line refers to. */
  rarity: PackOddsTier;
  /** Formatted rate string, e.g. "5%". */
  displayRate: string;
}

// ---------------------------------------------------------------------------
// PackTemplate — the canonical pack definition
// ---------------------------------------------------------------------------

/**
 * A complete pack definition in the catalog.
 * This is the "template" from which individual pack instances are sold /
 * opened. It owns both the display metadata and the resolution odds.
 */
export interface PackTemplate {
  // ---- Identity ----
  id: string;
  slug: string;
  name: string;
  description: string;

  // ---- Classification ----
  sport: string;
  setName: string;
  season: string;
  category: PackCategory;

  // ---- Pricing ----
  /** Price in smallest currency unit (cents). */
  priceInCents: number;
  /** ISO 4217 currency code, e.g. "USD". */
  currency: string;
  /** Original price before a sale (optional — used for strike-through display). */
  originalPriceInCents?: number;

  // ---- Contents ----
  totalCards: number;
  guaranteedSlots: GuaranteedSlot[];

  // ---- Odds (resolution) ----
  odds: PackOdds;

  // ---- Display ----
  images: PackImages;
  accentColor?: string;
  tags: string[];
  /** User-facing odds lines (decoupled from resolution odds). */
  oddsDisplay: PackOddsDisplay[];

  // ---- Availability ----
  status: PackStatus;
  availability: PackAvailability;

  // ---- Flags ----
  isFeatured: boolean;
  isLimited: boolean;

  // ---- Metadata ----
  // ---- Availability derived (storefront) ----
  /**
   * True when any tier with positive effective probability has zero eligible
   * in-pool inventory rows for this template (same predicate as `open` prefetch).
   */
  soldOut?: boolean;

  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  seoMeta?: PackSeoMeta;

  // ---- Pack builder (Phase 1) — nullable when unset / legacy ----
  poolFilter?: PoolFilterJson | null;
  distributionOverride?: DistributionOverrideJson | null;
  chaseCeilingMultiple?: number;
  expectedPackValueCents?: number | null;
  safetyCheckStatus?: SafetyCheckStatus;
  /** Admin override: storefront presents the pack as sold out and rips are refused. */
  forceSoldOut?: boolean;
  safetyCheckRanAt?: Date | null;
}

// ---------------------------------------------------------------------------
// Input / mutation types
// ---------------------------------------------------------------------------

/** Payload accepted when creating a new pack via the admin API. */
export interface CreatePackInput {
  slug?: string;
  name: string;
  description: string;
  sport: string;
  setName: string;
  season: string;
  category: PackCategory;
  priceInCents: number;
  /** ISO 4217 currency code. Defaults to `"USD"` when omitted. */
  currency?: string;
  originalPriceInCents?: number;
  totalCards: number;
  guaranteedSlots?: GuaranteedSlot[];
  odds: PackOdds;
  images: PackImages;
  accentColor?: string;
  tags?: string[];
  oddsDisplay?: PackOddsDisplay[];
  status?: PackStatus;
  availability?: Partial<PackAvailability>;
  isFeatured?: boolean;
  isLimited?: boolean;
  seoMeta?: PackSeoMeta;

  /** Builder: optional pool filter JSON (defaults to sport-only from `sport`). */
  poolFilter?: PoolFilterJson | null;
  distributionOverride?: DistributionOverrideJson | null;
  chaseCeilingMultiple?: number;
}

/**
 * Payload accepted when updating an existing pack. All fields optional.
 * Nested objects (`images`, `availability`) accept partial patches so callers
 * can update a single nested field without providing the whole object.
 * `odds` requires a full replacement since odds must always sum to 1.0.
 */
export type UpdatePackInput = Partial<
  Omit<CreatePackInput, "images" | "availability">
> & {
  images?: Partial<PackImages>;
  availability?: Partial<PackAvailability>;
};

// ---------------------------------------------------------------------------
// Projections
// ---------------------------------------------------------------------------

/** Lightweight summary returned in list / grid endpoints. */
export interface PackSummary {
  id: string;
  slug: string;
  name: string;
  sport: string;
  category: PackCategory;
  priceInCents: number;
  currency: string;
  status: PackStatus;
  thumbnail: string;
  isFeatured: boolean;
  isLimited: boolean;
  tags: string[];
}

// ---------------------------------------------------------------------------
// Legacy compatibility
// ---------------------------------------------------------------------------

/**
 * @deprecated Use PackTemplate instead.
 * Kept so existing code that imports `Pack` still compiles.
 */
export type Pack = PackTemplate;
